import moment from "moment/min/moment-with-locales";
import { Children, Suspense } from "react";
import { Stack } from "react-bootstrap";
import { InfoCircleFill } from "react-bootstrap-icons";
import { Await, useRouteLoaderData } from "react-router-dom";
import { findRadioDayparts, formatDaypartText, formatDemographicText, getRadioName } from "./HelperFunctions";
import RATooltip from "./RATooltip";
import { daysPerspectives } from "./constants";
import { RadioName } from "./helperRenders";

const DescriptionBase = ({ children, label = "" }) => (
    <Stack direction="horizontal" gap={1}>
        <span className=" text-nowrap">{label}:</span>
        {children}
    </Stack>
);
const DescriptionValue = ({ children, className = "" }) => <span className={`fw-bold ${className}`}>{children}</span>;
const DDSimple = ({ label = "", value, children }) => (
    <DescriptionBase label={label}>
        <DescriptionValue>{value ?? children}</DescriptionValue>
    </DescriptionBase>
);
const DDCustom = ({ children }) => children;
const DDRadioId = ({ radioId, label = "Station" }) => {
    return (
        <DescriptionBase label={label}>
            <DescriptionValue>
                <RadioName radioId={radioId} />
            </DescriptionValue>
        </DescriptionBase>
    );
};
const DDRadioIds = ({ radioIds }) => (
    <DescriptionBase label="Station(s)">
        <DescriptionValue>{radioIds.map(x => <RadioName key={x} radioId={x} />).reduce((prev, curr) => [prev, ", ", curr])}</DescriptionValue>
    </DescriptionBase>
);

const DDDate = ({ date }) => (
    <DescriptionBase label="Date">
        <DescriptionValue>{moment(date).format("L")}</DescriptionValue>
    </DescriptionBase>
);
const DDDateRange = ({ startDate, endDate }) => (
    <DescriptionBase label="Date period">
        <DescriptionValue>
            {startDate && endDate
                ? `${moment(startDate).format("L")} - ${moment(endDate).format("L")}`
                : `${moment(startDate).startOf("isoWeek").format("L")} - ${moment(startDate).endOf("isoWeek").format("L")}`}
        </DescriptionValue>
    </DescriptionBase>
);
const DDDatetimeRange = ({ startDatetime, endDatetime }) => (
    <DescriptionBase label="Time period">
        <DescriptionValue>
            {moment(startDatetime).format("L LT")} - {moment(endDatetime).format("L LT")}
        </DescriptionValue>
    </DescriptionBase>
);
const DDTimeRange = ({ startTime, endTime }) => (
    <DescriptionBase label="Time">
        <DescriptionValue>
            {moment(startTime, "HH:mm:ss").format("LT")} - {moment(endTime, "HH:mm:ss").format("LT")}
        </DescriptionValue>
    </DescriptionBase>
);
const DDHourRange = ({ startHour, endHour }) => {
    if (startHour === 0 && endHour >= 23) return null;
    return (
        <DescriptionBase label="Hours">
            <DescriptionValue>
                {startHour} - {endHour}
            </DescriptionValue>
        </DescriptionBase>
    );
};
const DDMinuteRange = ({ startMinute, endMinute }) => {
    if (startMinute === 0 && endMinute >= 59) return null;
    return (
        <DescriptionBase label="Minutes">
            <DescriptionValue>
                {startMinute} - {endMinute}
            </DescriptionValue>
        </DescriptionBase>
    );
};
const DDPerspective = ({ days }) => {
    if (!days) return null;
    return (
        <DescriptionBase label="Perspective">
            <DescriptionValue>
                {daysPerspectives.find(x => JSON.stringify(x.days) === JSON.stringify(days))?.perspective ??
                    `Custom - ${days.map(x => moment().weekday(x).format("dd")).join(", ")}`}
            </DescriptionValue>
        </DescriptionBase>
    );
};
const DDDataType = ({ dataType, radioId }) => {
    const { radios } = useRouteLoaderData("root");
    if (!dataType || !radioId) return null;
    return (
        <Suspense>
            <Await resolve={radios}>
                {resolvedRadios => {
                    const radio = resolvedRadios.data.find(x => x.radio_id === radioId);
                    if (!radio.stream || !radio.ppm) return null;
                    return (
                        <DescriptionBase label="Data Type">
                            <DescriptionValue>{dataType === 1 ? "Stream" : "Ratings"}</DescriptionValue>
                        </DescriptionBase>
                    );
                }}
            </Await>
        </Suspense>
    );
};
const DDDemo = ({ demoId }) => {
    const { demographics } = useRouteLoaderData("root");
    if (!demoId) return null;
    return (
        <Suspense>
            <Await resolve={demographics}>
                {resolvedDemos => (
                    <DescriptionBase label="Demographic">
                        <DescriptionValue>{formatDemographicText(resolvedDemos.data.find(d => d.demo_id === demoId))}</DescriptionValue>
                    </DescriptionBase>
                )}
            </Await>
        </Suspense>
    );
};
const TruncateList = ({ list, maxLength = 5 }) => {
    const truncatedList = list.slice(0, maxLength);
    const showTooltip = list.length > maxLength;
    return (
        <>
            <span className="d-block d-sm-none">{list[0]}</span>
            <div className="d-none d-sm-block">
                {truncatedList.map((x, i) => (
                    <span key={i}>
                        {x}
                        {i < truncatedList.length - 1 && ","}
                    </span>
                ))}
            </div>
            {showTooltip && (
                <>
                    <span>, ...</span>
                    <RATooltip tooltipElement={<InfoCircleFill />}>
                        <Stack className="fw-bold">
                            {list.map((x, i) => (
                                <span key={i}>{x}</span>
                            ))}
                        </Stack>
                    </RATooltip>
                </>
            )}
        </>
    );
};
const DDMarketRadios = ({ marketRadioIds }) => {
    const { allRadioNames } = useRouteLoaderData("root");
    if (!marketRadioIds?.length) return null;

    return (
        <Suspense>
            <Await resolve={allRadioNames}>
                {resolvedRadios => {
                    const marketRadios = marketRadioIds.map(x => getRadioName(x, resolvedRadios.data)).sort();
                    return (
                        <DescriptionBase label="Market Includes">
                            <DescriptionValue className=" d-flex column-gap-1">
                                <TruncateList list={marketRadios} />
                            </DescriptionValue>
                        </DescriptionBase>
                    );
                }}
            </Await>
        </Suspense>
    );
};
const DDMusicTestDaypart = ({ daypartId, dataType, radioId }) => {
    const { musicTestDayparts } = useRouteLoaderData("root");
    return (
        <Suspense>
            <Await resolve={musicTestDayparts}>
                {resolvedDayparts => {
                    <DescriptionBase label="Daypart">
                        <DescriptionValue>
                            {formatDaypartText(findRadioDayparts(resolvedDayparts.data, radioId, dataType).find(x => x.daypart_id === daypartId))}
                        </DescriptionValue>
                    </DescriptionBase>;
                }}
            </Await>
        </Suspense>
    );
};
const DDExcludedDays = ({ excludedDays }) => {
    if (!excludedDays?.length) return null;
    return (
        <DescriptionBase label="Excluded days">
            <DescriptionValue className=" d-flex column-gap-1">
                <TruncateList list={excludedDays.map(x => moment(x).format("L"))} />
            </DescriptionValue>
        </DescriptionBase>
    );
};

DataDescription.Custom = DDCustom;
DataDescription.Simple = DDSimple;
DataDescription.Station = DDRadioId;
DataDescription.Stations = DDRadioIds;
DataDescription.Date = DDDate;
DataDescription.DateRange = DDDateRange;
DataDescription.DatetimeRange = DDDatetimeRange;
DataDescription.TimeRange = DDTimeRange;
DataDescription.HourRange = DDHourRange;
DataDescription.MinuteRange = DDMinuteRange;
DataDescription.Perspective = DDPerspective;
DataDescription.Demo = DDDemo;
DataDescription.DataType = DDDataType;
DataDescription.MarketRadios = DDMarketRadios;
DataDescription.MusicTestDaypart = DDMusicTestDaypart;
DataDescription.ExcludedDays = DDExcludedDays;

export default function DataDescription({ className, children }) {
    const Divider = () => <div className="vr d-none d-xl-inline-block"></div>;

    const childrenWithSeparators = [];
    Children.forEach(children, (child, index) => {
        if (child) {
            childrenWithSeparators.push(child);
            childrenWithSeparators.push(<Divider key={index} />);
        }
    });
    childrenWithSeparators.pop();

    return (
        <Stack direction="horizontal" className={"flex-wrap  column-gap-2 " + className}>
            {childrenWithSeparators}
        </Stack>
    );
}
